import { useSelector } from "react-redux";
import { authSelectors } from "../../auth/store/authReducer";
import {
  subscriptionPlan,
  subscriptionPlaygroundLimits,
  subscriptionToolLimits,
  subscriptionUserLimits,
  subscriptionLiveChatMessageLimits,
} from "../pricingLimits";

// Contact page for enterprise customers who hit an individually-negotiated limit (there is no
// higher self-serve plan to upsell them to).
export const CONTACT_SALES_URL = "https://mcpshowcase.com/p/contact";

/**
 * Single source of truth for subscription-driven gating in the app.
 *
 * The backend serializes the current subscription on `user.subscription` with derived fields
 * (trialInProgress, daysLeft, planCode, paymentProblem, ...). This hook reads those and exposes:
 *  - isReadOnly: the trial ended (or a paid subscription lapsed) with no active plan -> the app is
 *    read-only. daysLeft === 0 is the backend's "expired" sentinel.
 *  - trialInProgress / daysLeft: for the header countdown badge.
 *  - hasPaymentProblem: an active paid subscription whose last charge failed.
 *  - can(feature): whether the current plan includes a feature ("liveChat").
 *  - limit(kind): the numeric plan limit for playgrounds/tools/users.
 *  - openUpgrade(plan): opens the FastSpring popup (via window.showPaymentWindow). When a
 *    targetPlan is given it builds that product path; when omitted (the "add card" / renew
 *    case) it uses the current user's server-provided sub.planCode verbatim -- never a
 *    hard-coded plan -- so an enterprise user gets the enterprise product, a pro user pro, etc.
 */
export const useSubscriptionGate = () => {
  const user = useSelector(authSelectors.getUser);
  const sub = user?.subscription;
  const plan = sub?.plan;

  const daysLeft = sub?.daysLeft ?? 9999;
  const trialInProgress = !!sub?.trialInProgress;
  const hasPaymentProblem = !!sub?.paymentProblem;

  // Read-only once the trial (or paid period) has fully elapsed with nothing active.
  // An active paid subscription is never read-only — even on renewal day (daysLeft 0),
  // before the next charge resets the counter (sub.paid stays true while the plan is active).
  const isReadOnly = !!sub && !sub.paid && daysLeft <= 0;

  // Prefer the backend-resolved per-subscription limits (subscription.limits: override-or-plan-default);
  // fall back to the static plan mirror only when the server didn't ship them (older sessions).
  const serverLimits = sub?.limits;
  const isEnterprise = plan === subscriptionPlan.ENTERPRISE;

  const can = (feature) => {
    if (feature === "liveChat") {
      const messages =
        serverLimits?.monthlyMessages ??
        subscriptionLiveChatMessageLimits[plan] ??
        0;
      return messages > 0;
    }
    return true;
  };

  const limit = (kind) => {
    switch (kind) {
      case "playgrounds":
        return serverLimits?.playgrounds ?? subscriptionPlaygroundLimits[plan] ?? 0;
      case "tools":
        return serverLimits?.tools ?? subscriptionToolLimits[plan] ?? 0;
      case "users":
        return serverLimits?.users ?? subscriptionUserLimits[plan] ?? 0;
      default:
        return 0;
    }
  };

  const productPath = (targetPlan, period = "monthly") =>
    `mcpshowcase-${targetPlan}-${period}`;

  const openUpgrade = (targetPlan) => {
    // Already-paying customers upgrade in place (server-side FastSpring proration) behind a
    // confirmation dialog instead of being sent back through checkout. Only clean paid subs
    // qualify: a failed-charge state (paymentProblem) needs the popup to fix the card first,
    // and a subscription pending cancellation has nothing to change.
    const canUpgradeInPlace =
      targetPlan && sub?.paid && !sub?.paymentProblem && !sub?.dateCanceled;
    if (canUpgradeInPlace && typeof window.showUpgradeConfirm === "function") {
      window.showUpgradeConfirm(targetPlan);
      return;
    }

    if (!user || typeof window.showPaymentWindow !== "function") {
      return;
    }
    const path = targetPlan ? productPath(targetPlan) : sub?.planCode;
    if (!path) {
      // No product to open (e.g. no subscription loaded). Surface the error modal
      // instead of pushing an undefined path into the FastSpring builder.
      console.warn("[FastSpring] openUpgrade called with no resolvable product path");
      window.fsShowError && window.fsShowError("payment_start_failed");
      return;
    }
    window.showPaymentWindow(user, path);
  };

  return {
    user,
    subscription: sub,
    plan,
    isEnterprise,
    contactSalesUrl: CONTACT_SALES_URL,
    daysLeft,
    trialInProgress,
    hasPaymentProblem,
    isReadOnly,
    can,
    limit,
    openUpgrade,
    productPath,
  };
};

export default useSubscriptionGate;
